import { motion } from "framer-motion";
import { ShieldCheck, MapPin, CreditCard } from "lucide-react";
import { formatMoney } from "../../utils/formatMoney";

export default function ReviewStep({ 
  policyData, 
  additionalDetails, 
  paymentMethod, 
  onEdit, 
  onConfirm 
}) {
  const formatAdditionalDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;
    
    const entries = Object.entries(details)
      .filter(([key, value]) => {
        // Filter out object values (like coverageDuration) and only keep primitive values
        return typeof value !== 'object' || value === null;
      })
      .map(([key, value]) => {
        const formattedKey = {
          bikeName: "Bike Name/Model",
          vehicleName: "Vehicle Name/Model",
          licensePlate: "License Plate Number",
        }[key] || key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .replace(/bus/i, 'Bus')
          .replace(/license/i, 'License')
          .replace(/plate/i, 'Plate')
          .replace(/vehicle/i, 'Vehicle')
          .replace(/departure/i, 'Departure')
          .replace(/destination/i, 'Destination')
          .replace(/region/i, 'Region')
          .replace(/city/i, 'City')
          .replace(/town/i, 'Town')
          .replace(/bike/i, 'Bike')
          .replace(/name/i, 'Name')
          .replace(/taxi/i, 'Taxi')
          .replace(/type/i, 'Type')
          .replace(/route/i, 'Route');
        
        return { key: formattedKey, value };
      });
    
    return entries;
  };

  const additionalDetailsFormatted = formatAdditionalDetails(additionalDetails);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="mb-6 text-center">
        <h3 className="mb-2 text-xl font-bold text-gray-900">Review Your Order</h3>
        <p className="text-sm text-gray-600">Please review all details before proceeding to payment</p>
      </div>

      {/* Policy Summary */}
      <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
        <div className="flex gap-3 items-center mb-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <h4 className="font-semibold text-emerald-800">Policy Details</h4>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-emerald-700">Policy Type:</span>
            <span className="font-medium text-emerald-900">{policyData.policyType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-emerald-700">Provider:</span>
            <span className="font-medium text-emerald-900">{policyData.providerName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-emerald-700">Cover:</span>
            <span className="font-medium text-emerald-900">{policyData.coverLabel}</span>
          </div>
          <div className="flex justify-between text-base font-semibold">
            <span className="text-emerald-700">Amount:</span>
            <span className="text-emerald-900">{formatMoney(policyData.amount || 0, "₵ ")}</span>
          </div>
        </div>
      </div>

      {/* Additional Details */}
      {additionalDetailsFormatted && additionalDetailsFormatted.length > 0 && (
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex gap-3 items-center mb-3">
            <MapPin className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-800">Additional Information</h4>
          </div>
          <div className="space-y-2 text-sm">
            {additionalDetailsFormatted.map(({ key, value }, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-blue-700">{key}:</span>
                <span className="font-medium text-blue-900">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Method */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex gap-3 items-center mb-3">
          <CreditCard className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold text-gray-800">Payment Method</h4>
        </div>
        <div className="text-sm">
          <div className="flex justify-between">
            <span className="text-gray-700">Method:</span>
            <span className="font-medium text-gray-900">
              {paymentMethod === "momo" ? "Mobile Money" : "Credit/Debit Card"}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onEdit}
          className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
        >
          Edit Details
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 px-4 py-3 font-semibold text-white bg-emerald-600 rounded-xl transition-colors hover:bg-emerald-700"
        >
          Proceed to Payment
        </button>
      </div>
    </motion.div>
  );
}
